import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import { EXHIBITIONS } from '../lib/data';
import { useLanguage } from '../lib/LanguageContext';
import { HOME_CONTENT } from '../lib/localizedContent';

const FeaturedExhibitions = () => {
  const { language } = useLanguage();
  const content = HOME_CONTENT[language];
  const featured = EXHIBITIONS.slice(0, 3);
  return (
    <section
      id="exhibitions"
      className="bg-bone text-charcoal py-24 md:py-32 lg:py-40 border-t border-[#E6E4DD]"
      data-testid="featured-exhibitions"
    >
      <div className="px-6 md:px-12 lg:px-16">
        <div className="flex items-end justify-between gap-6 mb-16 md:mb-24">
          <Reveal>
            <div>
              <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/60">
                {content.programme}
              </span>
              <h2
                className="mt-5 font-sans font-extrabold text-charcoal leading-[0.95] tracking-normal text-5xl md:text-6xl lg:text-7xl"
                data-testid="exhibitions-heading"
              >
                {content.featured}
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="hidden md:block">
            <Link to="/exhibitions" data-testid="exhibitions-archive-link" className="editorial-link">
              <span>{content.archive}</span>
              <span className="arrow">→</span>
            </Link>
          </Reveal>
        </div>

        <ul className="flex flex-col">
          {featured.map((ex, idx) => (
            <ExhibitionRow key={ex.id} ex={ex} idx={idx} viewLabel={content.view} />
          ))}
        </ul>
      </div>
    </section>
  );
};

const ExhibitionRow = ({ ex, idx, viewLabel }) => {
  return (
    <motion.li
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: idx * 0.05 }}
      className="group border-t border-[#E6E4DD] last:border-b"
      data-testid={`exhibition-row-${ex.id}`}
    >
      <Link
        to={`/exhibitions/${ex.id}`}
        className="grid grid-cols-12 gap-4 md:gap-8 items-start py-8 md:py-10 lg:py-12"
      >
        <div className="col-span-2 md:col-span-1">
          <span className="text-[11px] md:text-xs tracking-eyebrow uppercase text-charcoal/50">
            {ex.number}
          </span>
        </div>

        <div className="col-span-10 md:col-span-4">
          <h3
            className="font-sans font-extrabold text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-normal text-charcoal"
          >
            {ex.title}
          </h3>
          <p className="mt-3 text-[11px] tracking-eyebrow uppercase text-charcoal/60">
            {ex.dates}
          </p>
        </div>

        <div className="col-span-12 md:col-span-3 order-3 md:order-none">
          <p className="text-[13px] md:text-sm text-charcoal/70 leading-relaxed font-light">
            {ex.description}
          </p>
        </div>

        <div className="col-span-7 md:col-span-2 text-[11px] tracking-eyebrow uppercase text-charcoal/60 self-center">
          <div>{ex.location}</div>
          <div className="mt-2">
            <span className="inline-block px-2 py-1 border border-charcoal/30 text-charcoal/70">
              {ex.status}
            </span>
          </div>
        </div>

        <div className="col-span-5 md:col-span-2 self-center justify-self-end">
          <span className="editorial-link" data-testid={`exhibition-link-${ex.id}`}>
            <span className="hidden sm:inline">{viewLabel}</span>
            <span className="arrow">→</span>
          </span>
        </div>

        <div className="col-span-12 mt-4 md:mt-6 overflow-hidden">
          <div className={`relative w-full overflow-hidden bg-beige ${ex.imageFit === 'contain' ? '' : 'aspect-[16/8] md:aspect-[16/6]'}`} style={ex.imageFit === 'contain' ? { aspectRatio: ex.imageAspect || '41 / 18' } : undefined}>
            <motion.img
              src={ex.image}
              alt={ex.title}
              className={`absolute inset-0 w-full h-full ${ex.imageFit === 'contain' ? 'object-contain' : 'object-cover'}`}
              initial={{ scale: ex.imageFit === 'contain' ? 1 : 1.05 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
            />
            <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/10 transition-colors duration-700" />
          </div>
        </div>
      </Link>
    </motion.li>
  );
};

export default FeaturedExhibitions;
